import { i18n } from "~/i18n";
import { cn } from "~/lib/utils";
import { type ContextWindowSnapshot, formatContextWindowTokens } from "~/lib/contextWindow";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";

function formatPercentage(value: number | null): string | null {
  if (value === null || !Number.isFinite(value)) {
    return null;
  }
  if (value < 10) {
    return `${value.toFixed(1).replace(/\.0$/, "")}%`;
  }
  return `${Math.round(value)}%`;
}

export function ContextWindowMeter(props: {
  usage: ContextWindowSnapshot;
  providerDisplayName?: string | null;
}) {
  const { usage, providerDisplayName } = props;
  const maxTokens = usage.maxTokens ?? null;
  const rawPercentage =
    maxTokens !== null &&
    maxTokens > 0 &&
    usage.usedPercentage !== null &&
    Number.isFinite(usage.usedPercentage)
      ? usage.usedPercentage
      : null;
  const usedPercentage = formatPercentage(rawPercentage);
  const normalizedPercentage =
    rawPercentage === null ? 0 : Math.max(0, Math.min(100, rawPercentage));
  const radius = 9.75;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (normalizedPercentage / 100) * circumference;
  const totalProcessedTokens = usage.totalProcessedTokens ?? null;
  const showTotalProcessed = totalProcessedTokens !== null && totalProcessedTokens > 0;
  const usageState =
    rawPercentage === null
      ? "neutral"
      : normalizedPercentage >= 90
        ? "critical"
        : normalizedPercentage >= 70
          ? "warning"
          : "healthy";
  const usageColor =
    usageState === "critical"
      ? "var(--destructive)"
      : usageState === "warning"
        ? "var(--warning)"
        : usageState === "healthy"
          ? "var(--success)"
          : "color-mix(in oklab, var(--color-muted-foreground) 72%, transparent)";
  const usageStatusLabel =
    usageState === "critical"
      ? i18n.t("contextWindow.status.critical")
      : usageState === "warning"
        ? i18n.t("contextWindow.status.warning")
        : usageState === "healthy"
          ? i18n.t("contextWindow.status.healthy")
          : null;
  const formattedUsedTokens = formatContextWindowTokens(usage.usedTokens);

  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        delay={150}
        closeDelay={0}
        render={
          <button
            type="button"
            className={cn(
              "inline-flex size-7 cursor-pointer items-center justify-center rounded-full border border-transparent text-muted-foreground outline-none transition-colors",
              "hover:bg-accent data-[pressed]:bg-accent",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
            )}
            aria-label={
              usedPercentage !== null && usageStatusLabel !== null
                ? i18n.t("contextWindow.aria.percentageUsed", {
                    percentage: usedPercentage,
                    status: usageStatusLabel,
                  })
                : i18n.t("contextWindow.aria.tokensUsed", { tokens: formattedUsedTokens })
            }
          >
            <span className="relative flex size-5 items-center justify-center">
              <svg
                viewBox="0 0 24 24"
                className="-rotate-90 absolute inset-0 size-full transform-gpu"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r={radius}
                  fill="none"
                  stroke="color-mix(in oklab, var(--color-muted-foreground) 24%, transparent)"
                  strokeWidth="3"
                />
                <circle
                  cx="12"
                  cy="12"
                  r={radius}
                  fill="none"
                  stroke={usageColor}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  className="transition-[stroke,stroke-dashoffset] duration-500 ease-out motion-reduce:transition-none"
                />
              </svg>
            </span>
          </button>
        }
      />
      <PopoverPopup side="top" align="center" className="w-40 max-w-none" viewportClassName="p-0">
        <div className="flex flex-col items-center gap-1 px-3 py-2 text-center">
          <div className="font-medium text-muted-foreground text-xs">
            {i18n.t("contextWindow.title")}
          </div>
          {usedPercentage !== null ? (
            <>
              <div className="font-semibold text-foreground text-lg leading-none tabular-nums">
                {usedPercentage}
              </div>
              <div className="text-[11px] tabular-nums text-muted-foreground/70">
                {formattedUsedTokens} / {formatContextWindowTokens(maxTokens)}
              </div>
            </>
          ) : (
            <div className="font-semibold text-foreground text-sm tabular-nums">
              {i18n.t("contextWindow.tokensUsed", { tokens: formattedUsedTokens })}
            </div>
          )}
          {showTotalProcessed ? (
            <div className="mt-1 flex items-baseline justify-center gap-1.5 text-[11px] leading-4">
              <span className="text-muted-foreground/60">
                {i18n.t("contextWindow.totalProcessed")}
              </span>
              <span className="font-medium tabular-nums text-muted-foreground/80">
                {formatContextWindowTokens(totalProcessedTokens)}
              </span>
            </div>
          ) : null}
          {usage.compactsAutomatically ? (
            <div className="mt-1 text-pretty text-[11px] font-medium leading-4 text-muted-foreground/70">
              {providerDisplayName
                ? i18n.t("contextWindow.autoCompacts", { provider: providerDisplayName })
                : i18n.t("contextWindow.autoCompactsFallback")}
            </div>
          ) : null}
        </div>
      </PopoverPopup>
    </Popover>
  );
}
